import React from 'react';
import { API } from '../api';

/*
 * A poster at full size, over whatever you were looking at.
 *
 * Lists carry the stored 480px thumbnail — enough to recognise a poster,
 * nowhere near enough to read the date on one. So opening it fetches the real
 * image, and shows the thumbnail blurred behind it meanwhile: something
 * appears the instant you tap, and sharpens a moment later, rather than an
 * empty rectangle for a second and a half.
 *
 * This was written three times in three screens before it became a component.
 */
function PosterLightbox({ poster, meta, onClose, children }) {
  const [full, setFull] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const closeRef = React.useRef(null);
  const openerRef = React.useRef(null);

  React.useEffect(() => {
    // Give the keyboard somewhere to be, and put it back afterwards.
    openerRef.current = document.activeElement;
    if (closeRef.current) closeRef.current.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (openerRef.current && openerRef.current.focus) openerRef.current.focus();
    };
  }, [onClose]);

  React.useEffect(() => {
    let cancelled = false;
    setFull(null);
    setFailed(false);
    fetch(`${API}/api/posters/${poster.id}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled) { if (d && d.imageData) setFull(d.imageData); else setFailed(true); } })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [poster.id]);

  const src = full || poster.thumbData || poster.imageData || null;

  return (
    <div className="poster-zoom" role="dialog" aria-modal="true"
      aria-label={`${poster.title}, full size`} onClick={onClose}>
      <div className={`poster-zoom-inner${children ? ' poster-zoom-inner-details' : ''}`} onClick={e => e.stopPropagation()}>
        {src
          ? <img src={src} alt={poster.title}
              className={`poster-zoom-img${full ? '' : ' poster-zoom-img-soft'}`} />
          : <div className="poster-zoom-img poster-zoom-empty" />}

        <div className="poster-zoom-bar">
          <div className="poster-zoom-title">
            {poster.title}
            <span className="poster-zoom-meta">
              {meta || ''}
              {!full && !failed ? (meta ? ' · ' : '') + 'loading the full size…' : ''}
              {failed ? (meta ? ' · ' : '') + 'could not load the full size' : ''}
            </span>
          </div>
          <button type="button" ref={closeRef} className="w3-button w3-round poster-btn" onClick={onClose}>
            Close
          </button>
        </div>
        {/* The words behind the picture — the readable version of the poster,
            for the date without squinting and for a screen reader. */}
        {children && <div className="poster-zoom-details">{children}</div>}
      </div>
    </div>
  );
}

export default PosterLightbox;
